import { CommonModule } from '@angular/common';
import { Component, inject, PLATFORM_ID, OnInit, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { catchError, map, Observable, of, shareReplay, Subject, Subscription, takeUntil } from 'rxjs';
import { User } from '../../../user/models/user.model';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AuthService } from '../../../../core/services/auth.service';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatListModule } from '@angular/material/list';
import { PropertyService } from '../../../../features/property/services/property.service';
import { State, US_STATES } from '../../../general/models/us-states.model';
import { HapticsService } from '../../../general/services/haptics.service';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-bottom-nav',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatMenuModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatButtonModule,
    MatCardModule,
    MatFormField,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatListModule,
    RouterLink,
    RouterLinkActive
  ],
  templateUrl: './bottom-nav.html',
  styleUrl: './bottom-nav.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class BottomNav implements OnInit, OnDestroy {
  private breakpointObserver = inject(BreakpointObserver);
  dialog = inject(MatDialog);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private authService = inject(AuthService);
  private haptics = inject(HapticsService);

  isMobile = false;
  private destroyed = new Subject<void>();
  isSignedIn = false; 
  user: User | null = null;
  private userSubscription: Subscription | undefined;
  user$: Observable<User | null>;
  searchText = '';
  private propertyService = inject(PropertyService);
  states: State[] = US_STATES;
  navItems = [
    { icon: 'home', label: 'Home', path: '/' },
    { icon: 'search', label: 'Search', path: '/property' },
    { icon: 'favorite', label: 'Saved', path: '/profile/saved' },
  ];
  isMenuOpen = false;

  constructor() {
    this.user$ = this.authService.user$;
    // this.isSignedIn = this.authService.isAuthenticated();
  }

  ngOnInit() {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroyed))
      .subscribe((result: { matches: boolean; }) => {
        this.isMobile = result.matches;
      });

    this.userSubscription = this.authService.user$.subscribe((user) => {
      if (user) {
        this.isSignedIn = true;
        this.user = user;
      } else {
        this.isSignedIn = false;
        this.user = null;
      }
    });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  hasRole(roles: string[]): boolean {
    return !!this.user && roles.includes(this.user.role);
  }

  activeStates$ = this.propertyService.getActivePropertyStates().pipe(
    map((codes: string[]) =>
      this.states.filter(s => codes.includes(s.code))
    ),
    catchError(err => {
      console.error('Failed to load active states:', err);
      return of([]);
    }),
    shareReplay(1)
  );

  onAddClick(str: string) {
    this.haptics.click();
    console.log('in', str);
    if (str == 'home') {
      this.router.navigate(['/']);
    } else if (str == 'search') {
      this.router.navigate(['/property']);
    } else if (str == 'admin') {
      this.router.navigate(['/profile/admin']);
    } else if (str == 'sales') {
      this.router.navigate(['/sales/dashboard']);
    } else if (str == 'login') {
      this.router.navigate(['/login']);
    } else if (str == 'saved-properties') {
      this.router.navigate(['/profile/saved']);
    } else if (str == 'manage-properties') {
      this.router.navigate(['/profile/manage-properties']);
    } else if (str == 'logout') {
      this.authService.logout();
    } else if (str == 'profile') {
      this.router.navigate(['/profile']);
    }

    this.closeMenu();
  }

  onClick() {
    this.haptics.click();
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    this.haptics.click();
  }

  // Close menu when a navigation item is clicked
  closeMenu() {
    this.haptics.click();
    this.isMenuOpen = false;
  }

  isActive(route: string): boolean {
    // exact: true prevents '/' from matching everything
    return this.router.isActive(route, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  get userInitials(): string {
    if (!this.user || !this.user || !this.user.lastName) return '??';
    // const names = this.user.username.trim().split(/\s+/);
    // if (names.length === 1) {
    //   return names[0].charAt(0).toUpperCase();
    // }
    return (this.user?.firstName?.charAt(0) + (this.user?.lastName ? this.user.lastName.charAt(0) : '')).toUpperCase();
  }

  // Generate dynamic background color based on username
  get avatarColor(): string {
    if (!this.user || !this.user.username) return '#4a90e2'; // Default color
    let hash = 0;
    for (let i = 0; i < this.user.username.length; i++) {
      hash = this.user.username.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    //return `hsl(${hue}, 50%, 60%)`; // Dynamic HSL color
    return 'oklab(60% -0.3 -0.1)';
  }

  getResults() {
    console.log('getResults', this.searchText);
    if (this.searchText) {
      this.router.navigate(['/property'], {
        queryParams: { state: this.searchText }
      });
    } else {
      this.router.navigate(['/property']);
    }
  }
  
  goHome = () => { this.router.navigate(['/']); }
  goToProfile = () => { this.router.navigate(['/profile']); }
  goToManageProperties = () => { this.router.navigate(['/profile/manage-properties']); }
  openLogin = () => { this.router.navigate(['/login']); }
  goToSaved = () => { this.router.navigate(['/profile/saved']); }
  logout = () => {}
}
